import { FundAssetClass } from '@watershed/constants/finance';
// eslint-disable-next-line import/no-extraneous-dependencies
import { AssetCorporateFields } from '@watershed/db/models/finance/AssetCorporateModel';
// eslint-disable-next-line import/no-extraneous-dependencies
import { AssetGroupFields } from '@watershed/db/models/finance/AssetGroupModel';
// eslint-disable-next-line import/no-extraneous-dependencies
import { AssetPersonalMotorVehicleInsuranceFields } from '@watershed/db/models/finance/AssetPersonalMotorVehicleInsuranceModel';
// eslint-disable-next-line import/no-extraneous-dependencies
import { AssetCommercialRealEstateFields } from '@watershed/db/models/finance/AssetRealEstateModel';
// eslint-disable-next-line import/no-extraneous-dependencies
import { AssetSovereignBondFields } from '@watershed/db/models/finance/AssetSovereignBondModel';
// eslint-disable-next-line import/no-extraneous-dependencies
import AssetYearModel, {
  AssetYearFields,
} from '@watershed/db/models/finance/AssetYearModel';
// eslint-disable-next-line no-restricted-imports
import { GQOneSchemaDataFormat } from '../../generated/graphql-schema-types';
import invariant from 'invariant';

import { ValueOf } from '../../utils/utilTypes';
import AssetCorporate from '../classes/AssetCorporate';
import AssetGroup from '../classes/AssetGroup';
import AssetPersonalMotorVehicleInsurance from '../classes/AssetPersonalMotorVehicleInsurance';
import AssetRealEstate from '../classes/AssetRealEstate';
import AssetSovereignBond from '../classes/AssetSovereignBond';
import AssetYear from '../classes/AssetYear';
import { AssetYearFieldsTemp } from '../types/FinanceFragments';
import isNotNullish from '@watershed/shared-util/src/isNotNullish';

export type AnyAsset =
  | AssetCorporate
  | AssetRealEstate
  | AssetGroup
  | AssetSovereignBond
  | AssetPersonalMotorVehicleInsurance;

export enum FinanceModelsWithFlag {
  AssetCorporate = 'AssetCorporate',
  AssetRealEstate = 'AssetRealEstate',
  AssetGroup = 'AssetGroup',
  AssetSovereignBond = 'AssetSovereignBond',
  AssetPersonalMotorVehicleInsurance = 'AssetPersonalMotorVehicleInsurance',
  Fund = 'Fund',
  AssetYear = 'AssetYear',
  AssetHolding = 'AssetHolding',
}

export enum AssetTypeStrings {
  AssetCorporate = 'AssetCorporate',
  AssetRealEstate = 'AssetRealEstate',
  AssetGroup = 'AssetGroup',
  AssetSovereignBond = 'AssetSovereignBond',
  AssetPersonalMotorVehicleInsurance = 'AssetPersonalMotorVehicleInsurance',
}

const ASSET_TYPE_STRING_TO_FK_NAME = {
  [AssetTypeStrings.AssetCorporate]: 'assetCorporateId',
  [AssetTypeStrings.AssetRealEstate]: 'assetCommercialRealEstateId',
  [AssetTypeStrings.AssetGroup]: 'assetGroupId',
  [AssetTypeStrings.AssetSovereignBond]: 'assetSovereignBondId',
  [AssetTypeStrings.AssetPersonalMotorVehicleInsurance]:
    'assetPersonalMotorVehicleInsuranceId',
} as const satisfies Record<AssetTypeStrings, keyof AssetYearFields>;

export const ASSET_YEAR_ASSET_FOREIGN_KEY_IDS = Object.values(
  ASSET_TYPE_STRING_TO_FK_NAME
);

export type AssetTypeStringToFkName = typeof ASSET_TYPE_STRING_TO_FK_NAME;

// This type is used to ensure that the values of AssetTypeStringToFkName
// are fields on AssetYearFields
type CheckMapper<T extends Record<string, keyof AssetYearFields>> = T;
type _ = CheckMapper<AssetTypeStringToFkName>;

export type AssetTypeString = keyof typeof AssetTypeStrings;
export type AssetYearAssetForeignKeyId = ValueOf<AssetTypeStringToFkName>;
export type AssetTypeIdValue = Partial<{
  [p in AssetYearAssetForeignKeyId]: string;
}>;

export const customAssetYearTemplateAssetTypes: Array<AssetTypeString> = [
  'AssetSovereignBond',
  'AssetPersonalMotorVehicleInsurance',
];

export const standardAssetYearTemplateAssetTypes: Array<AssetTypeString> = [
  'AssetCorporate',
  'AssetRealEstate',
  'AssetGroup',
];

export const customAssetYearTemplateAssetForeignKeyIds: Array<AssetYearAssetForeignKeyId> =
  customAssetYearTemplateAssetTypes.map((assetType) =>
    getAssetIdFieldFromAssetType(assetType)
  );

export const customAssetHoldingTemplateAssetClasses: Array<FundAssetClass> = [
  FundAssetClass.PersonalMotorVehicleInsurance,
];

export const customAssetHoldingTemplateAssetTypes: Array<AssetTypeString> = [
  'AssetPersonalMotorVehicleInsurance',
];

export const AllAssetClasses = Object.values(FundAssetClass);

export const standardAssetHoldingTemplateAssetClasses = AllAssetClasses.filter(
  (ac) => !customAssetHoldingTemplateAssetClasses.includes(ac)
);

export const standardAssetHoldingTemplateAssetTypes: Array<AssetTypeString> = [
  'AssetCorporate',
  'AssetRealEstate',
  'AssetGroup',
  'AssetSovereignBond',
];

export type AssetClassFields =
  | AssetCorporateFields
  | AssetCommercialRealEstateFields
  | AssetGroupFields
  | AssetSovereignBondFields
  | AssetPersonalMotorVehicleInsuranceFields;

export const ASSET_TYPE_STRINGS_TO_ASSET_ID_PREFIX: Record<
  AssetTypeStrings,
  string
> = {
  [AssetTypeStrings.AssetCorporate]: 'fnh',
  [AssetTypeStrings.AssetRealEstate]: 'acre',
  [AssetTypeStrings.AssetGroup]: 'agp',
  [AssetTypeStrings.AssetSovereignBond]: 'asb',
  [AssetTypeStrings.AssetPersonalMotorVehicleInsurance]: 'apmvi',
};

export function getAssetTypeFromId(id: string): AssetTypeString {
  for (const [assetType, assetIdPrefix] of Object.entries(
    ASSET_TYPE_STRINGS_TO_ASSET_ID_PREFIX
  )) {
    if (id.startsWith(`${assetIdPrefix}_`)) {
      return assetType as AssetTypeString;
    }
  }
  throw new Error(`Invalid asset id: ${id}`);
}

export function isPmviAssetType(id: string): boolean {
  return (
    getAssetTypeFromId(id) ===
    AssetTypeStrings.AssetPersonalMotorVehicleInsurance
  );
}

export function isCorporateAssetType(id: string): boolean {
  return getAssetTypeFromId(id) === AssetTypeStrings.AssetCorporate;
}

export function isSovereignBondAssetType(id: string): boolean {
  return getAssetTypeFromId(id) === AssetTypeStrings.AssetSovereignBond;
}

export function isRealEstateAssetType(id: string): boolean {
  return getAssetTypeFromId(id) === 'AssetRealEstate';
}

export function getAssetForeignKeyFromId(
  id: string
): AssetYearAssetForeignKeyId {
  const assetType = getAssetTypeFromId(id);
  return ASSET_TYPE_STRING_TO_FK_NAME[assetType];
}

export const AssetOneSchemaDataFormats: Array<GQOneSchemaDataFormat> = [
  GQOneSchemaDataFormat.AssetCorporate,
  GQOneSchemaDataFormat.AssetGroup,
  GQOneSchemaDataFormat.AssetRealEstate,
  GQOneSchemaDataFormat.AssetSovereignBond,
  GQOneSchemaDataFormat.AssetPersonalMotorVehicleInsurance,
];

export function getAssetIdFromAssetType(
  typename: AssetTypeString,
  assetId: string
): AssetTypeIdValue {
  return { [ASSET_TYPE_STRING_TO_FK_NAME[typename]]: assetId };
}

export function getAssetIdFieldFromAssetType(
  typename: AssetTypeString
): AssetYearAssetForeignKeyId {
  return ASSET_TYPE_STRING_TO_FK_NAME[typename];
}

export function getDataFormatNames({
  shouldRenameFund,
}: {
  shouldRenameFund: boolean;
}): {
  [key in GQOneSchemaDataFormat]: string;
} {
  return {
    [GQOneSchemaDataFormat.Fund]: shouldRenameFund
      ? 'Line of business'
      : 'Fund',
    [GQOneSchemaDataFormat.AssetCorporate]: 'Corporate assets',
    [GQOneSchemaDataFormat.AssetRealEstate]: 'Real estate assets',
    [GQOneSchemaDataFormat.AssetSovereignBond]: 'Sovereign debt',
    [GQOneSchemaDataFormat.AssetPersonalMotorVehicleInsurance]:
      'Personal motor vehicle insurance',
    [GQOneSchemaDataFormat.AssetGroup]: 'Asset groups',
    [GQOneSchemaDataFormat.AssetYearDefault]: 'Annual data',
    [GQOneSchemaDataFormat.SovereignBondAssetYear]:
      'Sovereign debt annual data',
    [GQOneSchemaDataFormat.PersonalMotorVehicleInsuranceAssetYear]:
      'Personal motor vehicle insurance annual data',
    [GQOneSchemaDataFormat.AssetHoldingAll]: 'Asset holdings',
    [GQOneSchemaDataFormat.PersonalMotorVehicleInsuranceAssetHolding]:
      'Personal motor vehicle insurance holdings',
    [GQOneSchemaDataFormat.PointOfContact]: 'Points of contact',
    [GQOneSchemaDataFormat.SupplierContact]: 'Supplier contacts',
  };
}

export function getAssetIdFromAssetYear(
  assetYear: AssetYear | AssetYearModel | AssetYearFields | AssetYearFieldsTemp
): string {
  const assetId =
    assetYear.assetCorporateId ??
    assetYear.assetCommercialRealEstateId ??
    assetYear.assetGroupId ??
    assetYear.assetSovereignBondId ??
    assetYear.assetPersonalMotorVehicleInsuranceId;
  invariant(isNotNullish(assetId), 'AssetYear has no assetId');
  return assetId;
}

// This is the list of corporate asset classes that are allowed for holdings of asset groups + relevant for pcaf score 5 calculations
export const nonInsuranceCorporateAssetClasses = [
  FundAssetClass.ListedEquity,
  FundAssetClass.CorporateBonds,
  FundAssetClass.UnlistedEquity,
  FundAssetClass.BusinessLoans,
  FundAssetClass.ProjectFinance,
] as const;

// This is the list of all possible asset classes for holdings of corporate assets
export const corporateAssetClasses = [
  ...nonInsuranceCorporateAssetClasses,
  FundAssetClass.CommercialLineOfInsurance,
] as const;

// This is the list of all possible asset classes for holdings of real estate assets
export const realEstateAssetClasses = [
  FundAssetClass.CommercialRealEstate,
  FundAssetClass.Mortgages,
] as const;

export const assetGroupAssetClasses = [FundAssetClass.AssetGroup] as const;

export const sovereignDebtAssetClasses = [
  FundAssetClass.SovereignDebt,
] as const;

export const personalMotorVehicleInsuranceAssetClasses = [
  FundAssetClass.PersonalMotorVehicleInsurance,
] as const;

export const commercialLinesAssetClasses = [
  FundAssetClass.CommercialLineOfInsurance,
] as const;

const assetHoldingClassDisplayNames: Record<FundAssetClass, string> = {
  [FundAssetClass.AssetGroup]: 'Asset group',
  [FundAssetClass.ListedEquity]: 'Listed equity',
  [FundAssetClass.CorporateBonds]: 'Corporate bonds',
  [FundAssetClass.UnlistedEquity]: 'Unlisted equity',
  [FundAssetClass.BusinessLoans]: 'Business loans',
  [FundAssetClass.SovereignDebt]: 'Sovereign debt',
  [FundAssetClass.PersonalMotorVehicleInsurance]:
    'Personal motor vehicle insurance',
  [FundAssetClass.CommercialLineOfInsurance]: 'Commercial line of insurance',
  [FundAssetClass.CommercialRealEstate]: 'Commercial real estate',
  [FundAssetClass.Mortgages]: 'Mortgages',
  [FundAssetClass.ProjectFinance]: 'Project finance',
};

export const getAssetHoldingClassDisplayName = (assetClass: string): string => {
  if (assetClass in assetHoldingClassDisplayNames) {
    return assetHoldingClassDisplayNames[
      assetClass as keyof typeof FundAssetClass
    ];
  }
  return assetClass;
};

export const assetClassValidationMap: Record<
  AssetYearAssetForeignKeyId,
  { validAssetClasses: ReadonlyArray<FundAssetClass>; errorMsg: string }
> = {
  ['assetCorporateId']: {
    validAssetClasses: corporateAssetClasses,
    errorMsg:
      'Incorrect asset class provided - for corporate assets, holding asset class must be Listed equity, Corporate bonds, Unlisted equity, Business loans, Commercial lines of insurance, or Project finance',
  },
  ['assetCommercialRealEstateId']: {
    validAssetClasses: realEstateAssetClasses,
    errorMsg:
      'Incorrect asset class provided - for commercial real estate assets, holding asset class must be Commercial real estate or Mortgages',
  },
  ['assetGroupId']: {
    validAssetClasses: [FundAssetClass.AssetGroup],
    errorMsg:
      'Incorrect asset class provided - for asset groups, holding asset class must be Asset group.',
  },
  ['assetSovereignBondId']: {
    validAssetClasses: [FundAssetClass.SovereignDebt],
    errorMsg:
      'Incorrect asset class provided - for sovereign debt, holding asset class must be Sovereign debt',
  },
  ['assetPersonalMotorVehicleInsuranceId']: {
    validAssetClasses: [FundAssetClass.PersonalMotorVehicleInsurance],
    errorMsg:
      'Incorrect asset class provided - for personal motor vehicle insurance, holding asset class must be Personal motor vehicle insurance',
  },
};
